import type { ExploreQuery } from '~/data/templates'

/**
 * The pages the web opens over another page rather than in place of it, per
 * `/specs/app.spec.md`: templates, settings and its sections, and the lists of every workout
 * and every exercise.
 *
 * Which one is open rides in the URL rather than in a piece of state, because the router is
 * still on the page underneath: the dialog has an address of its own, opening one is an
 * ordinary link, and the browser's back closes it. The name is validated by the root route,
 * so every page in the app inherits the parameter and any of them can carry a dialog.
 */
export const DIALOG_NAMES = [
  'workouts',
  'exercises',
  'templates',
  'explore',
  'template',
  'settings',
  'units',
  'videos',
  'storage',
  'delete-account',
  'weighIn',
  'nutrition',
  'feedback',
  'weighin-weight',
  'weighin-add-photo',
  'metrics',
  'data',
  'export',
  'import',
] as const

export type DialogName = (typeof DIALOG_NAMES)[number]

/**
 * Which of the account's own panels a dialog name opens — the templates page, Explore, a
 * template's page, settings, feedback, the weigh-in — or null where it names none of them.
 * A settings section names settings, since a section is a page of settings rather than a
 * panel of its own.
 */
export function accountPanelOf(name: DialogName): DialogName | null {
  if (
    name === 'templates' ||
    name === 'explore' ||
    name === 'template' ||
    name === 'feedback' ||
    name === 'weighin-weight' ||
    name === 'weighin-add-photo' ||
    name === 'settings'
  ) {
    return name
  }
  return settingsSectionOf(name) === null ? null : 'settings'
}

/**
 * The sections settings holds, in the order the list offers them — and after them the two
 * pages the Data section leads on to, which are pages of settings without being sections of
 * its list.
 */
export const SETTINGS_SECTIONS = [
  'units',
  'videos',
  'weighIn',
  'nutrition',
  'storage',
  'data',
  'delete-account',
  'export',
  'import',
] as const

export type SettingsSection = (typeof SETTINGS_SECTIONS)[number]

/**
 * The path each of these wears in the address bar. The router is on the page underneath and
 * the dialog rides in its search parameters, so this path is a mask over that — see the web
 * app's `specs/tech.spec.md`. Every stub route below one of these paths redirects into the
 * state it stands for, so a link shared or a page reloaded lands on the same thing.
 */
export const DIALOG_PATHS = {
  workouts: '/all-workouts',
  exercises: '/all-exercises',
  templates: '/templates',
  explore: '/templates/explore',
  settings: '/settings',
  units: '/settings/units',
  videos: '/settings/videos',
  storage: '/settings/storage',
  'delete-account': '/settings/delete-account',
  weighIn: '/settings/weighin',
  nutrition: '/settings/nutrition',
  data: '/settings/data',
  export: '/settings/data/export',
  import: '/settings/data/import',
  feedback: '/feedback',
  'weighin-weight': '/weighin/weight',
  'weighin-add-photo': '/weighin/photos/add',
  metrics: '/metrics',
} as const satisfies Record<Exclude<DialogName, 'template'>, string>

/**
 * The address one of these wears, which for a template's page says which template — it is the
 * one of them that is about something rather than being one page there is only ever one of.
 */
export function dialogPath(name: DialogName, templateId?: string): string {
  if (name === 'template') {
    return templateId === undefined ? DIALOG_PATHS.templates : `/templates/${templateId}`
  }
  return DIALOG_PATHS[name]
}

/**
 * What the URL carries on behalf of whichever page is open over the current one: which page it
 * is, and — for Explore, whose filters have to survive opening a template and coming back —
 * what it is showing. Explore's query is one parameter of its own rather than five loose ones,
 * so closing Explore takes the whole of it with it rather than leaving `sort=recent` stuck to
 * every address you visit afterwards.
 */
export interface DialogSearch {
  readonly dialog?: DialogName
  readonly explore?: ExploreQuery
  /** Which day a weigh-in panel is on, as `YYYY-MM-DD`. Absent means today. */
  readonly day?: string
  /** Which template a template's page is about. */
  readonly template?: string
  /**
   * Which reading a panel is about. On the metrics panel that is what the chart draws —
   * `scale`, `trend`, `steps`, a measurement. On a weigh-in it is the field to open on:
   * `weight`, or the name of a measurement.
   */
  readonly measure?: string
  /** The way the goal process opens on, where something sent it there: `coached`. */
  readonly way?: 'coached'
  /**
   * The aim the goal process opens on, where something sent it there — reaching a goal and
   * choosing to aim the other way.
   */
  readonly aim?: 'lose' | 'gain'
}

/** The dialogs that can carry a day — the weigh-in panels, and nothing else. */
export const DAY_DIALOGS = [
  'weighin-weight',
  'weighin-add-photo',
] as const satisfies readonly DialogName[]

/** A day off the URL, where the URL carries one shaped like a day. */
export function dialogDayOf(value: unknown): string | null {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null
}

/** A name off the URL, where the URL carries one this app knows. */
export function dialogNameOf(value: unknown): DialogName | null {
  return DIALOG_NAMES.includes(value as DialogName) ? (value as DialogName) : null
}

export function settingsSectionOf(name: DialogName): SettingsSection | null {
  return SETTINGS_SECTIONS.includes(name as SettingsSection) ? (name as SettingsSection) : null
}

/**
 * What a link out of one of these pages carries: everything the URL held except the dialog.
 * A validated search parameter survives an ordinary navigation, so without this the page you
 * opened would follow you onto the page you asked it for — and asking for one is what closes
 * the other.
 */
export function withoutDialog<T extends DialogSearch>({
  dialog: _closed,
  explore: _shown,
  day: _day,
  template: _about,
  measure: _measure,
  // The way and the aim the goal process was sent to open on are read once, by that process.
  way: _way,
  aim: _aim,
  ...rest
}: T): Omit<T, 'dialog' | 'explore' | 'day' | 'template' | 'measure' | 'way' | 'aim'> &
  DialogSearch {
  return rest
}

/** Explore's query off the URL, whatever it holds, taking only what it recognises. */
export function exploreQueryOf(value: unknown): ExploreQuery | null {
  if (typeof value !== 'object' || value === null) {
    return null
  }
  const held = value as Record<string, unknown>
  return {
    search: typeof held.search === 'string' ? held.search : undefined,
    schedule: typeof held.schedule === 'string' ? held.schedule : undefined,
    workouts: typeof held.workouts === 'number' ? held.workouts : undefined,
    exercise: typeof held.exercise === 'string' ? held.exercise : undefined,
    sort: held.sort === 'popular' ? 'popular' : held.sort === 'recent' ? 'recent' : undefined,
  }
}
